import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Box,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getAllCampaignEpisodes } from '../../services/campaignEpisodeService';
import { getAllJournalEntries } from '../../services/bennyJournalService'; // To get journal titles
import { getUsersWithCampaignEmailNotifications, isAdminEmailNotificationSystemEnabled } from '../../services/userService';
import { sendCampaignEpisodeNotifications } from '../../services/emailService';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import YouTubeIcon from '@mui/icons-material/YouTube';
import BookIcon from '@mui/icons-material/MenuBook'; // Journal icon
import VisibilityIcon from '@mui/icons-material/Visibility'; // For "View Details" button
import EmailIcon from '@mui/icons-material/Email'; // For email notification button

const CampaignEpisodesList = () => {
  const [episodes, setEpisodes] = useState([]);
  const [journalEntries, setJournalEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { firestoreUser } = useAuth();
  // Email notification state
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [selectedEpisode, setSelectedEpisode] = useState(null);
  const [sendingEmails, setSendingEmails] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState('');
  const [emailError, setEmailError] = useState('');
  const [adminEmailSystemEnabled, setAdminEmailSystemEnabled] = useState(false);
  const [previewRecipients, setPreviewRecipients] = useState([]);

  const isAdmin = firestoreUser && firestoreUser.roles && firestoreUser.roles.admin;
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError('');
      try {
        const [episodesData, journalsData] = await Promise.all([
          getAllCampaignEpisodes(),
          getAllJournalEntries() // Fetch all journal entries
        ]);
        setEpisodes(episodesData);
        setJournalEntries(journalsData);
        
        // Check if admin email system is enabled for admins
        if (isAdmin && firestoreUser?.uid) {
          const emailSystemEnabled = await isAdminEmailNotificationSystemEnabled(firestoreUser.uid);
          setAdminEmailSystemEnabled(emailSystemEnabled);
        }
      } catch (err) {
        console.error("Error fetching campaign episodes or journal entries:", err);
        setError('Failed to load data. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [isAdmin, firestoreUser?.uid]);

  const formatRecipientList = (users) => {
    if (!users || users.length === 0) return '';
    
    if (users.length <= 3) {
      // Show all names if 3 or fewer
      return users.map(user => user.name || user.email).join(', ');
    } else if (users.length <= 10) {
      // Show first 2 names and count for 4-10 users
      const firstTwo = users.slice(0, 2).map(user => user.name || user.email).join(', ');
      return `${firstTwo} and ${users.length - 2} others`;
    } else {
      // Show just the count for 10+ users
      return `${users.length} recipients`;
    }
  };
  const handleSendEmailNotification = async (episode) => {
    setSelectedEpisode(episode);
    setEmailError('');
    
    try {
      // Fetch users to show preview in dialog
      const users = await getUsersWithCampaignEmailNotifications();
      setPreviewRecipients(users);
      setEmailDialogOpen(true);
    } catch (err) {
      console.error('Error fetching users for email preview:', err);
      setEmailError('Failed to load email recipients. Please try again.');
    }
  };

  const confirmSendEmails = async () => {
    if (!selectedEpisode) return;

    try {
      setSendingEmails(true);
      setEmailError('');
      setEmailSuccess('');      // Use already-fetched users from preview
      const users = previewRecipients;
      
      if (users.length === 0) {
        setEmailError('No users have email notifications enabled for campaign episodes.');
        return;
      }      // Send notifications
      await sendCampaignEpisodeNotifications(selectedEpisode, users, firestoreUser);
      
      const recipientList = formatRecipientList(users);
      setEmailSuccess(`Email notifications sent successfully to ${users.length} user(s): ${recipientList}`);
      setEmailDialogOpen(false);
    } catch (err) {
      console.error('Error sending email notifications:', err);
      setEmailError('Failed to send email notifications. Please try again.');
    } finally {
      setSendingEmails(false);
    }
  };
  const closeEmailDialog = () => {
    setEmailDialogOpen(false);
    setSelectedEpisode(null);
    setPreviewRecipients([]);
    setEmailError('');
    setEmailSuccess('');
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress sx={{ color: '#d98d2a' }} />
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ '.MuiAlert-message': { color: 'white' }, backgroundColor: '#5c3c10' }}>{error}</Alert>
      </Container>
    );
  }
  return (
    <Container 
      maxWidth="xl"
      sx={{ mt: 4, color: 'white', px: { xs: 2, sm: 3, md: 4 } }}
    >
      <Box sx={{ 
        maxWidth: { xs: '100%', sm: '900px', md: '1100px', lg: '1300px', xl: '1500px' }, 
        margin: '0 auto'
      }}>
      {/* Success/Error Messages */}
      {emailSuccess && (
        <Alert severity="success" sx={{ mb: 2, '.MuiAlert-message': { color: 'white' }, backgroundColor: '#2e7d32' }}>
          {emailSuccess}
        </Alert>
      )}
      {emailError && (
        <Alert severity="error" sx={{ mb: 2, '.MuiAlert-message': { color: 'white' }, backgroundColor: '#5c3c10' }}>
          {emailError}
        </Alert>
      )}

      <Box 
        sx={{ 
          mb: 3, 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2
        }}
      >
        <Typography 
          variant="h4" 
          component="h1"
          sx={{ 
            fontFamily: '"Georgia", serif',
            color: '#d98d2a'
          }}
        >
          Campaign Log & Chronicles
        </Typography>        
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            startIcon={<AddCircleOutlineIcon />}
            onClick={() => navigate('/campaign-episodes/create')}
            sx={{
              fontFamily: '"Georgia", serif',
              backgroundColor: '#d98d2a',
              color: '#1c1c1c',
              '&:hover': { backgroundColor: '#c87d26' },
              textTransform: 'none',
              fontSize: '1rem',
              padding: '8px 16px'
            }}
          >
            Log New Episode
          </Button>
        </Box>
      </Box>

      {episodes.length === 0 ? (
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant="h6" sx={{ fontFamily: '"Georgia", serif' }}>
            No campaign episodes logged yet.
          </Typography>
          <Typography sx={{ fontFamily: '"Georgia", serif', mt: 1, color: '#A9A9A9' }}>
            Be the first to document your adventures!
          </Typography>
        </Box>
      ) : (
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { 
            xs: '1fr', 
            md: 'repeat(2, minmax(0, 1fr))' 
          },
          gap: 3,
          alignItems: 'start',
          mt: 2,
          width: '100%'
        }}>
          {episodes.map((episode) => (
            <Card
              key={episode.id}
              onClick={() => navigate(`/campaign-episodes/${episode.id}`)}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                border: '1px solid #555',
                backgroundColor: '#3c3c3c',
                width: '100%',
                maxWidth: '100%',
                minHeight: '280px',
                height: '280px',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                cursor: 'pointer',
                overflow: 'hidden',
                boxSizing: 'border-box',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '4px 4px 12px rgba(0, 0, 0, 0.15)'
                }
              }}
            >
              <CardContent sx={{ 
                flexGrow: 1, 
                display: 'flex', 
                flexDirection: 'column',
                pb: 1,
                height: '100%',
                overflow: 'hidden'
              }}>
                <Typography 
                  variant="h6" 
                  component="h2" 
                  className="post-title"
                  sx={{ 
                    fontFamily: '"Georgia", serif', 
                    color: '#d98d2a', 
                    fontSize: '1.1rem',
                    fontWeight: 'bold',
                    mb: 0.5,
                    borderBottom: 'none',
                    lineHeight: 1.3,
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word'
                  }}
                >
                  {episode.episodeNumber ? `Ep. ${episode.episodeNumber}: ` : ''}{episode.title}
                </Typography>
                <Typography 
                  variant="caption" 
                  display="block" 
                  className="post-meta"
                  sx={{ 
                    mb: 1, 
                    fontStyle: 'italic', 
                    color: '#A9A9A9', 
                    fontFamily: '"Georgia", serif',
                    fontSize: '0.75rem'
                  }}
                >
                  Logged: {episode.createdAt?.toDate ? new Date(episode.createdAt.toDate()).toLocaleDateString() : 'Date unknown'}
                </Typography>
                {episode.description && (
                  <Typography 
                    variant="body2"
                    className="post-content-summary"
                    sx={{ 
                      color: '#E0E0E0', 
                      fontFamily: '"Georgia", serif',
                      lineHeight: 1.5,
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      fontSize: '0.85rem',
                      mb: 1,
                      flexGrow: 1
                    }}
                  >
                    {episode.description}
                  </Typography>
                )}
                {!episode.description && (
                  <Box sx={{ flexGrow: 1 }} />
                )}
              </CardContent>
              
              {/* Action buttons at the bottom */}
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'row',
                flexWrap: 'wrap',
                gap: 1,
                p: 2,
                pt: 0,
                borderTop: '1px solid #555',
                mt: 'auto'
              }}>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<VisibilityIcon />}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/campaign-episodes/${episode.id}`);
                  }}
                  sx={{
                    fontFamily: '"Georgia", serif',
                    backgroundColor: '#d98d2a',
                    color: '#1c1c1c',
                    '&:hover': { 
                      backgroundColor: '#c87d26'
                    },
                    textTransform: 'none',
                    fontSize: '0.75rem',
                    padding: '4px 10px',
                    flex: '1 1 auto'
                  }}
                >
                  Details
                </Button>

                {episode.youtubeVideoUrl && (
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<YouTubeIcon />}
                    href={episode.youtubeVideoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                      fontFamily: '"Georgia", serif',
                      backgroundColor: '#FF0000',
                      color: '#FFFFFF',
                      '&:hover': { 
                        backgroundColor: '#D40000'
                      },
                      textTransform: 'none',
                      fontSize: '0.75rem',
                      padding: '4px 10px',
                      flex: '1 1 auto'
                    }}
                  >
                    Video
                  </Button>
                )}

                {episode.relatedBennyJournalId && (
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<BookIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      const journalEntry = journalEntries.find(j => j.id === episode.relatedBennyJournalId);
                      if (journalEntry && journalEntry.downloadURL) {
                        navigate(`/view-pdf/${journalEntry.id}`, { 
                          state: { 
                            pdfUrl: journalEntry.downloadURL,
                            journalTitle: journalEntry.title,
                            from: '/campaign-episodes'
                          } 
                        });
                      } else {
                        navigate(`/benny-journal/${episode.relatedBennyJournalId}`, {
                          state: { from: '/campaign-episodes' }
                        });
                      }
                    }}
                    sx={{
                      fontFamily: '"Georgia", serif',
                      backgroundColor: '#66bb6a',
                      color: '#FFFFFF',
                      '&:hover': { 
                        backgroundColor: '#4caf50'
                      },
                      textTransform: 'none',
                      fontSize: '0.75rem',
                      padding: '4px 10px',
                      flex: '1 1 auto'
                    }}
                  >
                    Journal
                  </Button>
                )}

                {/* Email Notification Button - Admin Only AND Email System Enabled */}
                {isAdmin && adminEmailSystemEnabled && (
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<EmailIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSendEmailNotification(episode);
                    }}
                    sx={{
                      fontFamily: '"Georgia", serif',
                      backgroundColor: '#2196F3',
                      color: '#FFFFFF',
                      '&:hover': { 
                        backgroundColor: '#1976D2'
                      },
                      textTransform: 'none',
                      fontSize: '0.75rem',
                      padding: '4px 10px',
                      flex: '1 1 auto'
                    }}
                  >
                    Email
                  </Button>
                )}
              </Box>
            </Card>
          ))}
        </Box>
      )}

      {/* Email Notification Confirmation Dialog */}
      <Dialog
        open={emailDialogOpen}
        onClose={closeEmailDialog}
        aria-labelledby="email-dialog-title"
      >
        <DialogTitle id="email-dialog-title">
          Send Email Notifications
        </DialogTitle>        <DialogContent>
          <DialogContentText>
            Are you sure you want to send email notifications about "{selectedEpisode?.title}" to users who have enabled campaign episode notifications?
          </DialogContentText>
          
          {previewRecipients.length > 0 ? (
            <DialogContentText sx={{ mt: 2, color: '#1976d2' }}>
              <strong>Recipients ({previewRecipients.length}):</strong><br />
              {formatRecipientList(previewRecipients)}
            </DialogContentText>
          ) : (
            <DialogContentText sx={{ mt: 2, color: '#d32f2f' }}>
              <strong>No users have opted in to email notifications.</strong>
            </DialogContentText>
          )}
          
          <DialogContentText sx={{ mt: 2, fontWeight: 'bold' }}>
            This action cannot be undone and will send emails immediately.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEmailDialog} disabled={sendingEmails}>
            Cancel
          </Button>          <Button 
            onClick={confirmSendEmails} 
            disabled={sendingEmails || previewRecipients.length === 0}
            variant="contained"
            sx={{ backgroundColor: '#2196F3' }}
          >
            {sendingEmails ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
            {sendingEmails ? 'Sending...' : 'Send Emails'}
          </Button>
        </DialogActions>
      </Dialog>
      </Box>
    </Container>
  );
};

export default CampaignEpisodesList;
